import { connect, FormikValues } from 'formik'
import { get } from 'lodash'
import { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import SelectComponent from '@/components/Form/Select'
import {
  StyledWandIcon,
  VariableInputContainer,
  VariableLinkButton
} from '@/pages/SpaceAppBuilder/components/StyledComponents'
import VariableInput from '@/pages/SpaceAppBuilder/components/VariableInput/VariableInput'
import VariableOverlay from '@/pages/SpaceAppBuilder/components/VariableInput/VariableOverlay'
import VariableAutoComplete from '@/pages/SpaceAppBuilder/components/VariableInputDropdown'
import { FilterProps } from '@/pages/SpaceAppBuilder/FeaturesPage/Operations/Boolean/Conditions/interfaces'
import { Operator } from '@/pages/SpaceAppBuilder/FeaturesPage/Operations/Boolean/Conditions/utils'
import messages from '@/pages/SpaceAppBuilder/FeaturesPage/Operations/messages'
import { FieldTypes, Variable } from '@/pages/SpaceAppBuilder/utilities/interfaces'

// Bit lame - Select proptypes don't play nice with TS types
const Select: any = SelectComponent
interface FilterSectionProps {
  name: string
  filterData: FilterProps
  variables?: any
  readOnly?: boolean
  leftComparatorAsTextInput?: boolean
  leftComparatorPlaceholder?: string
}

const FilterContent = styled.div`
  margin-top: 10px;
  width: 90%;
  justify-content: start;
  align-items: flex-start;
  display: flex;
  > div {
    padding-right: 10px;
    .form__field {
      margin-bottom: 8px;
    }
  }
`

const SelectOperator = styled(Select)`
  height: 48px;
  width: 105px;
  select {
    min-width: 4rem;
    padding: 0.6rem;
  }
`

const ClauseColumn = styled.div`
  font-size: 14px;
  flex: 1 0 5%;
  max-width: 30px;
  width: 30px;
  margin-top: 22px;
`

const LeftComparatorColumn = styled.div`
  width: 260px;
  max-width: 260px;
  min-width: 260px;
`

const OperatorColumn = styled.div`
  max-width: 120px;
  width: 120px;
`

const RightComparatorColumn = styled.div`
  width: 260px;
  flex-grow: 0;
`

const StyledVariableOverlay = styled(VariableOverlay)`
  && {
    width: 340px;
    /* margin-top: -8px; */
  }
`

const StyledRightCompartorVariableOverlay = styled(StyledVariableOverlay)`
  && {
    width: 340px;
    margin-left: -100px;
    /* margin-top: -8px; */
  }
`

const StyledVariableInput = styled(VariableInput)`
  > div {
    > div {
      > div {
        height: 48px;
      }
    }
  }
`

const StyledVariableInputContainer = styled(VariableInputContainer)`
  padding-bottom: 0px;
`

const StyledVariableAutoComplete = styled(VariableAutoComplete)`
  && {
    && {
      width: 250px;
      height: 48px;
      &.form__filter-select {
        margin-top: 8px;
        .field__wrapper .ui.fluid.dropdown {
          display: flex;
          align-items: center;
        }
      }
    }
  }
`

const StyledRightCompartorVariableAutoComplete = styled(StyledVariableAutoComplete)`
  &.form__filter-select {
    margin-top: 8px;
    .field__wrapper .ui.fluid.dropdown {
      .menu {
        margin-left: -103px;
        width: 348px;
      }
    }
  }
`
export const Filter = connect(
  ({
    name,
    formik,
    filterData,
    variables,
    firstColumn = 'If',
    readOnly = false,
    leftComparatorAsTextInput = false,
    leftComparatorPlaceholder
  }: FilterSectionProps & FormikValues) => {
    const intl = useIntl()

    const { operator } = filterData
    const { setFieldValue, setFieldTouched } = formik

    const [showVariablePopOver, setShowVariablePopOver] = useState<string[]>([])

    const onVariablePopOverClick = (variablePopoverKey: string) => {
      setShowVariablePopOver((prev: string[]) => {
        if (prev.includes(variablePopoverKey)) {
          return prev.filter(accordionkey => accordionkey !== variablePopoverKey)
        } else {
          return prev.concat(variablePopoverKey)
        }
      })
    }

    const addVariableFromOverlay = useCallback(
      (name: string, variable: Variable) => {
        formik.setFieldValue(name, variable.name)
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [name, formik]
    )

    return (
      <FilterContent>
        <ClauseColumn>{firstColumn}</ClauseColumn>
        <LeftComparatorColumn>
          <StyledVariableInputContainer>
            {leftComparatorAsTextInput ? (
              <StyledVariableInput
                variables={[]}
                readOnly={readOnly}
                placeholder={leftComparatorPlaceholder || intl.formatMessage(messages.leftComparator)}
                validVariableTypes={[FieldTypes.STRING, FieldTypes.ANY]}
                name={`${name}.leftComparator`}
              />
            ) : (
              <StyledVariableAutoComplete
                readOnly={readOnly}
                placeholder={intl.formatMessage(messages.leftComparator)}
                name={`${name}.leftComparator`}
                variables={variables}
                onSelectOption={() => {
                  setTimeout(() => {
                    setFieldValue(`${name}.rightComparator`, '')
                    setFieldTouched(`${name}.rightComparator`, false)
                  }, 1)
                }}
                showVariableButton={false}
                showValidationMessage={true}
              />
            )}

            {showVariablePopOver.includes(`${name}.leftComparator`) && (
              <StyledVariableOverlay
                onSelectVariable={(variable: Variable) => addVariableFromOverlay(`${name}.leftComparator`, variable)}
                onClose={() =>
                  setShowVariablePopOver((prev: string[]) =>
                    prev.filter(accordionkey => accordionkey !== `${name}.leftComparator`)
                  )
                }
                variables={variables}
              />
            )}
          </StyledVariableInputContainer>
          {!readOnly && !leftComparatorAsTextInput && (
            <VariableLinkButton onClick={() => onVariablePopOverClick(`${name}.leftComparator`)}>
              <>
                <StyledWandIcon />
                <span>{intl.formatMessage(messages.addVariableButton)}</span>
              </>
            </VariableLinkButton>
          )}
        </LeftComparatorColumn>
        <OperatorColumn>
          <SelectOperator
            disabled={readOnly}
            name={`${name}.operator`}
            onChange={
              operator === Operator.PR
                ? () => {
                    setFieldValue(`${name}.rightComparator`, '')
                  }
                : formik.handleChange
            }
          >
            <Select.Option key={Operator.EQ} value={Operator.EQ}>
              {intl.formatMessage(messages.equalsOperator)}
            </Select.Option>
            <Select.Option key={Operator.NE} value={Operator.NE}>
              {intl.formatMessage(messages.notEqualOperator)}
            </Select.Option>
            <Select.Option key={Operator.GT} value={Operator.GT}>
              {intl.formatMessage(messages.greaterThanOperator)}
            </Select.Option>
            <Select.Option key={Operator.GE} value={Operator.GE}>
              {intl.formatMessage(messages.greaterThanOrEqualOperator)}
            </Select.Option>
            <Select.Option key={Operator.LT} value={Operator.LT}>
              {intl.formatMessage(messages.lessThanOperator)}
            </Select.Option>
            <Select.Option key={Operator.LE} value={Operator.LE}>
              {intl.formatMessage(messages.lessThanOrEqualOperator)}
            </Select.Option>
            <Select.Option key={Operator.CO} value={Operator.CO}>
              {intl.formatMessage(messages.containsOperator)}
            </Select.Option>
            <Select.Option key={Operator.PR} value={Operator.PR}>
              {intl.formatMessage(messages.hasValueOperator)}
            </Select.Option>
            <Select.Option key={Operator.SW} value={Operator.SW}>
              {intl.formatMessage(messages.startsWithOperator)}
            </Select.Option>
            <Select.Option key={Operator.EW} value={Operator.EW}>
              {intl.formatMessage(messages.endsWithOperator)}
            </Select.Option>
          </SelectOperator>
        </OperatorColumn>
        {operator !== Operator.PR && (
          <RightComparatorColumn data-testid="rightComparator">
            <VariableInputContainer>
              <>
                <StyledRightCompartorVariableAutoComplete
                  readOnly={readOnly}
                  placeholder={intl.formatMessage(messages.rightComparator)}
                  name={`${name}.rightComparator`}
                  allowAdditions={true}
                  defaultValue={get(formik.values, `${name}.rightComparator`)}
                  variableWithEnumOptionsToInclude={get(formik.values, `${name}.leftComparator`)}
                  variables={variables}
                  showVariableButton={false}
                  showValidationMessage={true}
                />
                {showVariablePopOver.includes(`${name}.rightComparator`) && (
                  <StyledRightCompartorVariableOverlay
                    onSelectVariable={(variable: Variable) =>
                      addVariableFromOverlay(`${name}.rightComparator`, variable)
                    }
                    onClose={() =>
                      setShowVariablePopOver((prev: string[]) => {
                        return prev.filter(accordionkey => accordionkey !== `${name}.rightComparator`)
                      })
                    }
                    variables={variables}
                  />
                )}
                {!readOnly && (
                  <VariableLinkButton
                    onClick={() => {
                      onVariablePopOverClick(`${name}.rightComparator`)
                    }}
                  >
                    <>
                      <StyledWandIcon />
                      <span>{intl.formatMessage(messages.addVariableButton)}</span>
                    </>
                  </VariableLinkButton>
                )}
              </>
            </VariableInputContainer>
          </RightComparatorColumn>
        )}
      </FilterContent>
    )
  }
)
